import { PaymentData, ApplePayConfiguration } from '@adyen/adyen-web';

import { AppchargeErrorType, EPaymentProvider, PaymentMethod } from '../../../../../../contracts';

export enum AdyenCheckoutErrorTypes {
  NETWORK_ERROR = 'NETWORK_ERROR',
  CANCEL = 'CANCEL',
  IMPLEMENTATION_ERROR = 'IMPLEMENTATION_ERROR',
  API_ERROR = 'API_ERROR',
  ERROR = 'ERROR',
  SCRIPT_ERROR = 'SCRIPT_ERROR',
  SDK_ERROR = 'SDK_ERROR',
}

// CANCEL is intentionally omitted: it is handled separately as a silent dismiss
// (see ACDEV-24170 acceptance criteria).
const ADYEN_SDK_ERROR_TO_APPCHARGE_ERROR: Partial<
  Record<AdyenCheckoutErrorTypes, AppchargeErrorType>
> = {
  [AdyenCheckoutErrorTypes.NETWORK_ERROR]: AppchargeErrorType.CONNECTION,
  [AdyenCheckoutErrorTypes.IMPLEMENTATION_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.API_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.SCRIPT_ERROR]: AppchargeErrorType.GENERIC,
  [AdyenCheckoutErrorTypes.SDK_ERROR]: AppchargeErrorType.GENERIC,
};

export function mapAdyenSdkErrorToAppchargeError(
  sdkErrorName: string | undefined,
): AppchargeErrorType {
  if (!sdkErrorName) return AppchargeErrorType.GENERIC;
  return (
    ADYEN_SDK_ERROR_TO_APPCHARGE_ERROR[sdkErrorName as AdyenCheckoutErrorTypes] ??
    AppchargeErrorType.GENERIC
  );
}

export const APPLE_PAY_CONFIG: ApplePayConfiguration = {
  isExpress: true,
  showPayButton: true,
  requiredBillingContactFields: ['email'],
  requiredShippingContactFields: ['email'],
  domainName: getTopLevelDomain(),
};

export interface ApplePayPaymentDetailsRef {
  email: string;
  paymentMethodDetails: ApplePayJS.ApplePayPaymentMethod;
}

export function createAdyenApplePayFinalizeRequestBody(
  AdyenPaymentMethod: PaymentData,
  applePaymentDetails: ApplePayPaymentDetailsRef,
  ACPaymentMethod: PaymentMethod,
): any {
  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: applePaymentDetails.email,
    paymentMethodDisplayName: ACPaymentMethod?.paymentMethodDisplayName!,
    paymentMethodId: ACPaymentMethod?.id!,
    params: {
      paymentMethod: AdyenPaymentMethod.paymentMethod,
      paymentMethodDetails: applePaymentDetails.paymentMethodDetails,
    },
  };
}

// Apple Pay merchant validation requires `domainName` (Apple's
// `initiativeContext`) to equal the TOP-LEVEL document's domain - the address
// bar the player sees - not the checkout iframe's own origin. (ACDEV-25945)
//
// Safari Private/Incognito Browsing strips `document.referrer` to '' inside
// cross-origin iframes, so the previous `document.referrer || location.href`
// fallback resolved to the iframe's own host (pay.appcharge.com). Adyen then
// merchant-validated the wrong domain and PassKit rejected the otherwise-valid
// session ("Application failed to provide a valid merchant session"), breaking
// Apple Pay in Private Browsing while normal browsing kept working.
//
// `location.ancestorOrigins` is NOT stripped in Private Browsing and gives the
// real top-level origin, so prefer it and only fall back to referrer.
export function getTopLevelDomain(): string {
  try {
    if (window.self === window.top) {
      return window.location.hostname;
    }
    const ancestorOrigins = window.location.ancestorOrigins;
    if (ancestorOrigins?.length) {
      return new URL(ancestorOrigins[ancestorOrigins.length - 1]).hostname;
    }
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
    return window.location.hostname;
  } catch {
    return window.location.hostname;
  }
}
